// Shared driver for running a custom action against MANY selected list rows.
//
// Every list surface (designer Live Preview, in-app Run, the downloaded-app
// runtime) wires bulk the same way: clear the action's confirmation gate ONCE
// for the whole selection (the engine returns handler='confirm' on the first,
// un-answered call — the same loop a single action uses), then show the
// per-record outcomes in a BulkResultModal. This hook owns that loop + the
// modal state so each host only supplies the transport (`call`) — mirroring how
// RunActionBar composes the single-record confirm flow.
//
// Note: the engine reports each row's non-blocking WARNING as a ``skipped``
// row inside the bulk_result (never a batch-level 422), so — unlike the single
// action runner — there is no warning-confirm wrapper here: confirmation is
// genuinely one-time.

import { useCallback, useState } from 'react'
import BulkResultModal from './BulkResultModal'
import {
  useActionConfirm, attemptActionWithConfirm, isActionConfirmCancelled,
  type ConfirmPayload,
} from './ActionConfirmModal'
import type { RunActionResponse } from './contracts'
import type { TxnNotifier } from './context'

// The transport a host supplies: run the bulk action with an optional
// confirmation answer, resolving with either handler='confirm' (gate, re-call
// with the answer) or handler='bulk_result' (the outcomes).
export type BulkCall = (args: { confirm?: ConfirmPayload }) => Promise<RunActionResponse>

interface PendingResult {
  title: string
  results: NonNullable<RunActionResponse['results']>
  summary?: RunActionResponse['summary']
  labelFor?: (txnId: string) => string
}

export function useBulkAction(opts: {
  notify: TxnNotifier
  // Called once a batch completes (any outcome) — typically refresh the list,
  // since successful rows mutated data.
  onDone?: () => void
}): {
  // Run a bulk action. ``title`` heads the results modal; ``call`` is the
  // host transport; ``labelFor`` optionally prettifies each record id.
  // ``intercept`` sees the final response FIRST (after the confirmation gate)
  // and returns true when it handled it — a 'send' bulk run answers ONE send
  // window over the eligible ids, never a per-row results modal.
  runBulk: (
    title: string,
    call: BulkCall,
    labelFor?: (txnId: string) => string,
    intercept?: (res: RunActionResponse) => boolean,
  ) => Promise<void>
  busy: boolean
  bulkModals: React.ReactNode
} {
  const { confirmAction, actionConfirmModal } = useActionConfirm()
  const [result, setResult] = useState<PendingResult | null>(null)
  const [busy, setBusy] = useState(false)

  const runBulk = useCallback(async (
    title: string, call: BulkCall, labelFor?: (txnId: string) => string,
    intercept?: (res: RunActionResponse) => boolean,
  ) => {
    setBusy(true)
    try {
      const res = await attemptActionWithConfirm(
        (confirm) => call({ confirm }),
        confirmAction,
      )
      if (intercept && intercept(res)) return
      if (res.handler === 'bulk_result') {
        setResult({
          title, results: res.results || [], summary: res.summary, labelFor,
        })
        opts.onDone?.()
      } else if (res.message) {
        opts.notify.success(res.message)
        opts.onDone?.()
      }
    } catch (err) {
      if (isActionConfirmCancelled(err)) return // user backed out of the gate
      const msg = bulkErrorMessage(err)
      opts.notify.error(msg)
    } finally {
      setBusy(false)
    }
  }, [confirmAction, opts])

  const bulkModals = (
    <>
      {result && (
        <BulkResultModal
          title={result.title}
          results={result.results}
          summary={result.summary}
          labelFor={result.labelFor}
          onClose={() => setResult(null)}
        />
      )}
      {actionConfirmModal}
    </>
  )

  return { runBulk, busy, bulkModals }
}

// A batch only errors as a whole on a hard failure (auth / unknown action /
// the confirmation answer being wrong); per-row problems live inside
// bulk_result. Pull a readable line out of either error envelope.
function bulkErrorMessage(err: unknown): string {
  const e = err as {
    response?: { data?: { error?: { message?: string; details?: { errors?: Array<{ message?: string }> } } } }
    detail?: { summary?: string; errors?: Array<{ message?: string }> } | string
  }
  const env = e?.response?.data?.error
  if (env) {
    const first = env.details?.errors?.[0]?.message
    if (first) return first
    if (env.message) return env.message
  }
  const d = e?.detail
  if (typeof d === 'string' && d) return d
  if (d && typeof d === 'object') {
    const first = d.errors?.[0]?.message
    if (first) return first
    if (d.summary) return d.summary
  }
  return 'Could not run the action on the selected records.'
}
